import { useRef } from 'react'
import type { ReactNode } from 'react'
import { matchPath, Navigate, useLocation } from 'react-router-dom'
import { PageSpinningLoader } from '@joyful/ui/core'
import {
  CurrentUserProtectedRoute,
  DemoModeRedactionProvider,
  getDefaultAppRoutePath,
  getNavbarAppHref,
  InitialPageLoadComplete,
  useActiveAccount,
  useCurrentUser,
  useHasFeatureFlag,
  useHasUserCapability,
  PageTitle,
} from '@joyful/ui/app'
import type { NavbarApp } from '@joyful/ui/app'
import { ActiveOrgProvider } from '@/components/layouts/ActiveOrgProvider'
import { OrgSelectionRedirect } from '@/features/navigation/components/OrgSelectionRedirect'
import { useNotifyExtensionActiveAccount } from '@/features/auth/hooks/useNotifyExtensionActiveAccount'
import { getPathOrganizationScope } from '@/features/auth/utils/getPathOrganizationScope'
import { ErrorRoute } from '@/routes/ErrorRoute'
import { isUuid } from '@joyful/utils'

interface AuthProviderLayoutProps {
  children: ReactNode
}

export function AuthProviderLayout({ children }: AuthProviderLayoutProps) {
  return (
    <CurrentUserProtectedRoute>
      <DemoModeRedactionProvider>
        <PageTitle />
        <ActiveOrgProvider>
          <AuthProviderLayoutInner>{children}</AuthProviderLayoutInner>
        </ActiveOrgProvider>
      </DemoModeRedactionProvider>
    </CurrentUserProtectedRoute>
  )
}

function AuthProviderLayoutInner({ children }: AuthProviderLayoutProps) {
  const currentUser = useCurrentUser()
  const activeAccount = useActiveAccount()
  const hasCapability = useHasUserCapability()
  const hasFeatureFlag = useHasFeatureFlag()
  const location = useLocation()
  useNotifyExtensionActiveAccount()

  if (hasCapability('app.app.view') === false) {
    if (hasCapability('app.workspace.view')) {
      return (
        <ExternalAppRedirectRoute
          app="workspace"
          path={getDefaultAppRoutePath({
            app: 'workspace',
            hasCapability,
            hasFeatureFlag,
            orgSlug: activeAccount?.slug ?? null,
          })}
        />
      )
    }

    if (hasCapability('app.bpo.view')) {
      const bpoEncounterPath = getBpoEncounterCounterpartPath({
        hash: location.hash,
        pathname: location.pathname,
        search: location.search,
      })
      const defaultBpoPath = getDefaultAppRoutePath({
        app: 'bpo',
        hasCapability,
        hasFeatureFlag,
        orgSlug: activeAccount?.slug ?? null,
      })
      const bpoPath = bpoEncounterPath ?? defaultBpoPath

      return <ExternalAppRedirectRoute app="bpo" path={bpoPath} />
    }

    if (hasCapability('app.internal.view')) {
      return (
        <ExternalAppRedirectRoute
          app="internal"
          path={getDefaultAppRoutePath({
            app: 'internal',
            hasCapability,
            hasFeatureFlag,
            orgSlug: activeAccount?.slug ?? null,
          })}
        />
      )
    }

    return (
      <InitialPageLoadComplete>
        <ErrorRoute error={404} />
      </InitialPageLoadComplete>
    )
  }

  if (currentUser.tos_accepted !== true && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />
  }

  if (
    getPathOrganizationScope(location.pathname) !== null &&
    activeAccount === null
  ) {
    return (
      <OrgSelectionRedirect
        targetPath={`${location.pathname}${location.search}${location.hash}`}
      />
    )
  }

  return <InitialPageLoadComplete>{children}</InitialPageLoadComplete>
}

function getBpoEncounterCounterpartPath({
  hash,
  pathname,
  search,
}: {
  hash: string
  pathname: string
  search: string
}) {
  const match = matchPath('/:orgSlug/:encounterId', pathname)
  const encounterId = match?.params.encounterId

  if (!isUuid(encounterId)) {
    return null
  }

  return `${pathname}${search}${hash}`
}

function ExternalAppRedirectRoute({
  app,
  path,
}: {
  app: NavbarApp
  path: string
}) {
  const href = getNavbarAppHref({ app, path })
  const hasStartedRedirectRef = useRef(false)

  return (
    <div
      ref={(element) => {
        if (element === null || hasStartedRedirectRef.current) {
          return
        }

        hasStartedRedirectRef.current = true
        window.location.replace(href)
      }}
    >
      <PageSpinningLoader />
    </div>
  )
}
